import {
  ARM_AUSSEN,
  arrowPolygon,
  exitDirection,
  HERKUNFT_R,
  INSEL_R,
  KNOTEN_R,
  MITTE,
  PFEIL_BASIS,
  PUNKT_R,
  RING_R,
  STRICH_FAHRT,
  STRICH_WEG,
  polar,
  tulipArms,
  tulipShapeInfo,
  tulipText,
} from '../lib/tulip'
import type { Tulip } from '../lib/types'

/**
 * Eine Tulpe als Zeichnung: die Notation des Rallye-Roadbooks, selbst gezeichnet.
 *
 * Alles daran ist SVG mit Strichen und gefüllten Flächen im Vordergrund. Das
 * ist keine Geschmacksfrage: Hintergrundfarben druckt der Browser nur, wenn im
 * Druckdialog "Hintergrundgrafiken" angehakt ist - Striche und Füllungen
 * dagegen immer. Und es gibt genau eine Farbe, currentColor. Was auf einer
 * Schwarzweisskopie unterschieden werden muss, wird deshalb durch die
 * Strichbreite unterschieden und nicht durch den Farbton: der zu fahrende Weg
 * ist der dicke, alles andere der dünne.
 *
 * Das Zeichen wird im Buch auf rund 19 mm gesetzt. Jedes Mass hier ist an
 * dieser Grösse gewählt und nicht an der Ansicht auf dem Bildschirm - was bei
 * 300 % schön aussieht und bei 19 mm zu Matsch wird, ist wertlos. Konkret
 * heisst das: keine Schrift im Zeichen, keine Schraffur, keine Linie unter 1.4
 * Einheiten (rund 0.27 mm), und höchstens vier Bauteile je Form - die
 * durchlaufende Spur mit Punkt und Pfeil zählt dabei als eines.
 *
 * Die Fähre ist die Form, die diese vier ausschöpft: Spur, Wasser, Rumpf,
 * Aufbau. Sie ist damit auch die Antwort auf die Frage, wie viel ein Zeichen
 * verträgt - der Aufbau kam dazu, weil der Rumpf allein sich als Schale las,
 * und ein fünftes Bauteil hätte bei 19 mm nichts mehr hinzugefügt, sondern nur
 * die vier anderen enger zusammengeschoben.
 *
 * Eine Ausnahme steht hier trotzdem: TulipPrinzip, die Tafel der Legendenseite.
 * Sie zeigt ein Zeichen dreimal so gross und beschriftet seine Bauteile - sie
 * ist keine Fahranweisung, sondern die Anleitung dazu, und wird im Sitzen
 * gelesen.
 */
export function TulipSketch({ tulip, className }: { tulip: Tulip; className?: string }) {
  const knoten = tulipShapeInfo(tulip.shape).group === 'knoten'

  return (
    <svg
      className={className ? `tulpe ${className}` : 'tulpe'}
      viewBox="0 0 100 100"
      role="img"
      aria-label={tulipText(tulip)}
    >
      {knoten ? <Knoten tulip={tulip} /> : <Strecke tulip={tulip} />}
    </svg>
  )
}

/**
 * Eine leere Tulpe zum Ausfüllen mit dem Kugelschreiber.
 *
 * Sie füllt die übrigen Zeilen einer Roadbookseite - dieselbe Antwort auf
 * ungenutzten Platz wie die Notizlinien im übrigen Buch: die Reise weicht von
 * der Planung ab, und ein vorgedruckter Kreis ist unterwegs mehr wert als eine
 * leere Fläche. Dünn gezeichnet, damit sie neben den gesetzten Tulpen als
 * Formular zu erkennen ist und nicht als Aussage.
 */
export function TulipBlank() {
  return (
    <svg className="tulpe tulpe--leer" viewBox="0 0 100 100" aria-hidden="true">
      <circle cx={MITTE} cy={MITTE} r={KNOTEN_R} fill="none" stroke="currentColor" />
      <line
        x1={MITTE}
        y1={MITTE + KNOTEN_R}
        x2={MITTE}
        y2={MITTE + HERKUNFT_R}
        stroke="currentColor"
      />
      <circle
        cx={MITTE}
        cy={MITTE + HERKUNFT_R}
        r={PUNKT_R * 0.7}
        fill="none"
        stroke="currentColor"
      />
    </svg>
  )
}

/** Die eine Tulpe, an der die Legende das Bauprinzip vorführt. */
const LEHRSTUECK: Tulip = { km: Number.NaN, shape: 'kreuzung', turn: 90, note: '' }

/**
 * Das Bauprinzip, an einem Zeichen erklärt: welcher Strich was sagt.
 *
 * Eine Legende, die fünfzehn Zeichen einzeln erklärt, hilft bei genau fünfzehn
 * Zeichen. Diese Zeichnung erklärt stattdessen, woraus alle fünfzehn gebaut
 * sind - dann versteht man auch eines, das man noch nie gesehen hat, und darum
 * geht es: unterwegs kommt eine Form dazu, weil eine Strasse gesperrt ist.
 *
 * Gewählt ist die Kreuzung mit einem Pfeil nach rechts, weil sie alle Bauteile
 * gleichzeitig zeigt: den Punkt, den dicken Weg, den Pfeil, den Kreis und zwei
 * dünne Striche daneben. Beschriftet sind drei davon; der Kreis steht im Text
 * neben der Tafel, weil eine vierte Führungslinie durch das Zeichen selbst
 * laufen müsste.
 *
 * Hier steht ausdrücklich Schrift im Bild, anders als in den Zeichen selbst.
 * Der Grund ist die Grösse: die Zeichen werden auf 19 mm gesetzt und müssen
 * ohne Schrift auskommen, diese Tafel ist rund dreimal so gross und wird im
 * Sitzen gelesen und nicht im Fahren. Die Beschriftungen sind kurz gehalten,
 * damit sie in einer Zeile stehen - eine umbrechende Beschriftung würde die
 * Führungslinie verlassen, auf die sie zeigt.
 *
 * Das Koordinatensystem ist 300 auf 104; das Zeichen selbst sitzt darin in
 * seinen gewohnten 100 auf 100, um 2 nach rechts und 2 nach unten gerückt.
 * Deshalb stehen unten Zahlen wie 52 und 94 und nicht 50 und 92. Die Tafel ist
 * fast dreimal so breit wie hoch, und das ist eine Platzfrage: sie steht auf
 * der Legendenseite neben ihrem Erklärtext und über den Zeichen, die eine
 * Richtung nennen - und die müssen mit ihr zusammen auf eine Seite.
 */
export function TulipPrinzip({ className }: { className?: string }) {
  return (
    <svg
      className={className ? `tulpe-lehre ${className}` : 'tulpe-lehre'}
      viewBox="0 0 300 104"
      role="img"
      aria-label={
        'Wie ein Zeichen gebaut ist: unten der Punkt, aus dem man kommt, ' +
        'darüber der dicke Weg durch die Kreuzung bis zum Pfeil, der sagt, wo es ' +
        'weitergeht, und daneben dünn die anderen Strassen.'
      }
    >
      <g transform="translate(2 2)">
        <Knoten tulip={LEHRSTUECK} />
      </g>

      <g fill="none" strokeLinecap="round">
        {/* Der obere dünne Arm - die Strasse, die man hier nicht nimmt. */}
        <LehrZeile y={18} von={58} text="die anderen Wege" />
        {/* Die Pfeilspitze. */}
        <LehrZeile y={52} von={106} text="so geht es weiter" />
        {/* Der Punkt am unteren Ende der Zufahrt. */}
        <LehrZeile y={94} von={60} text="hier kommst du her" />
      </g>
    </svg>
  )
}

/**
 * Eine Führungslinie mit ihrer Beschriftung.
 *
 * Die Linie beginnt dicht neben dem Bauteil, auf das sie zeigt, und läuft
 * waagrecht bis an eine gemeinsame Kante - so stehen die drei Beschriftungen
 * untereinander und nicht treppenförmig um das Zeichen herum.
 */
function LehrZeile({ y, von, text }: { y: number; von: number; text: string }) {
  /** Wo die Führungslinien enden und die Beschriftungen anfangen. */
  const rand = 196

  return (
    <>
      <line x1={von} y1={y} x2={rand} y2={y} strokeWidth={1.2} stroke="currentColor" />
      <text x={rand + 4} y={y + 3.6} fontSize={10} fill="currentColor" stroke="none">
        {text}
      </text>
    </>
  )
}

/**
 * Ein Knotenpunkt: Kreis, Zufahrt mit Punkt, die Arme, einer davon mit Pfeil.
 *
 * Der Punkt sitzt am unteren Ende der Zufahrt und nicht am Kreis: dort ist er
 * das, was er sagt - die Stelle, aus der man kommt - und er kollidiert nicht
 * mit der Kreislinie, die bei 19 mm nur einen Viertelmillimeter daneben läge.
 *
 * Die Zufahrt ist dick gezeichnet, so dick wie der Arm mit dem Pfeil.
 *
 * Das ist keine Verzierung, sondern die Regel dieses Zeichensatzes, endlich
 * auch hier angewandt: der zu fahrende Weg ist der dicke, alles andere der
 * dünne. Die Zufahrt ist der zu fahrende Weg - man ist ihn eben gefahren. Bei
 * den Streckenzeichen war das immer schon so (Durchfahrt zeichnet die ganze
 * Strasse dick), bei den Knoten war die Zufahrt dünn wie die Strassen, die man
 * gerade nicht nimmt.
 *
 * Der Gewinn ist der ganze Punkt dieser Änderung: es gibt jetzt einen
 * durchgehenden dicken Weg vom Punkt durch den Knoten bis in die Pfeilspitze.
 * Wer die Notation nicht kennt, muss nicht wissen, was der Punkt bedeutet - er
 * sieht eine Fahrspur, die unten anfängt und oben mit einem Pfeil aufhört, und
 * daneben ein paar dünne Striche, die es an dieser Stelle auch noch gibt. Genau
 * das war vorher mehrdeutig: bei einem Zeichen mit drei Armen war ohne
 * Vorwissen nicht zu sehen, welcher die Zufahrt ist - und wer die Zufahrt
 * verwechselt, spiegelt die Anweisung, aus rechts wird links.
 *
 * Es kostet keinen zusätzlichen Strich, nur eine andere Breite auf einem, der
 * ohnehin da ist. Das ist der Grund, warum diese Antwort und nicht ein zweiter,
 * kleiner Pfeil an der Zufahrt: zwei Pfeile in einem Zeichen von 19 mm werfen
 * die Frage auf, welcher von beiden die Anweisung ist.
 */
function Knoten({ tulip }: { tulip: Tulip }) {
  const kreisel = tulip.shape === 'kreisel'
  const radius = kreisel ? RING_R : KNOTEN_R
  const arme = tulipArms(tulip.shape, tulip.turn)
  const fahrt = exitDirection(tulip.shape, tulip.turn)

  return (
    <g fill="none" stroke="currentColor" strokeLinecap="round">
      <line
        x1={MITTE}
        y1={MITTE + radius}
        x2={MITTE}
        y2={MITTE + HERKUNFT_R}
        strokeWidth={STRICH_FAHRT}
      />
      <circle cx={MITTE} cy={MITTE + HERKUNFT_R} r={PUNKT_R} fill="currentColor" stroke="none" />

      <circle cx={MITTE} cy={MITTE} r={radius} strokeWidth={STRICH_WEG} />
      {/*
        Die Insel ist der ganze Unterschied zwischen Kreisverkehr und Kreuzung.
        Gefüllt und deutlich grösser als eine Strichstärke: auf einer Kopie
        muss dieser Unterschied als erstes zu sehen sein, denn er entscheidet,
        ob man anhält oder sich einfädelt.
      */}
      {kreisel && <circle cx={MITTE} cy={MITTE} r={INSEL_R} fill="currentColor" stroke="none" />}

      {arme.map((winkel) => (
        <Arm key={winkel} winkel={winkel} innen={radius} fahrt={winkel === fahrt} />
      ))}
    </g>
  )
}

/** Ein Weg, der vom Knoten abgeht. Der zu fahrende ist dick und trägt den Pfeil. */
function Arm({ winkel, innen, fahrt }: { winkel: number; innen: number; fahrt: boolean }) {
  const [x1, y1] = polar(winkel, innen)
  const [x2, y2] = polar(winkel, fahrt ? PFEIL_BASIS + 2 : ARM_AUSSEN)

  return (
    <>
      <line x1={x1} y1={y1} x2={x2} y2={y2} strokeWidth={fahrt ? STRICH_FAHRT : STRICH_WEG} />
      {fahrt && <polygon points={arrowPolygon(winkel)} fill="currentColor" stroke="none" />}
    </>
  )
}

/** Wo die durchgehende Strasse eines Streckenzeichens anfängt und aufhört. */
const UNTEN = MITTE + HERKUNFT_R
const OBEN = 22

/**
 * Eine Stelle der Strecke: die Strasse läuft durch, daneben steht das Merkmal.
 *
 * Kein Kreis, denn hier ist kein Knoten - es gibt nichts zu wählen. Der Punkt
 * unten und der Pfeil oben bleiben trotzdem: sie sagen, in welche Richtung die
 * Zeile gelesen wird, und ohne sie wäre ein Tunnel von unten nicht von einem
 * Tunnel von oben zu unterscheiden.
 */
function Strecke({ tulip }: { tulip: Tulip }) {
  const rampe = tulip.shape === 'auffahrt' || tulip.shape === 'abfahrt'
  const links = rampe && exitDirection(tulip.shape, tulip.turn) === 270

  return (
    <g
      fill="none"
      stroke="currentColor"
      strokeLinecap="round"
      strokeLinejoin="round"
      // Die Spiegelung ist die ganze Seitenwahl der beiden Rampen. Zwei
      // gezeichnete Fassungen wären zwei Stellen, an denen ein Mass abweichen
      // kann, für ein Zeichen, das sich genau darin unterscheidet.
      transform={links ? 'translate(100 0) scale(-1 1)' : undefined}
    >
      {inhalt(tulip)}
    </g>
  )
}

function inhalt(tulip: Tulip) {
  switch (tulip.shape) {
    case 'grenze':
      return (
        <>
          {/*
            Strich-Punkt quer durchs Bild: das Zeichen für eine Grenze auf
            jeder Karte, die je gedruckt wurde. Welche Grenze es ist und was
            dort vorzuzeigen ist, steht im Hinweis daneben - Text im Zeichen
            wäre bei 19 mm ohnehin nicht zu lesen.
          */}
          <line
            x1={4}
            y1={MITTE}
            x2={96}
            y2={MITTE}
            strokeWidth={STRICH_WEG}
            strokeDasharray="9 4 2.5 4"
          />
          <Durchfahrt />
        </>
      )

    case 'maut':
      return (
        <>
          {/* Dach über der Spur, links und rechts ein Häuschen. */}
          <line x1={10} y1={38} x2={90} y2={38} strokeWidth={2.6} />
          <rect x={12} y={42} width={22} height={24} strokeWidth={STRICH_WEG} />
          <rect x={66} y={42} width={22} height={24} strokeWidth={STRICH_WEG} />
          <Durchfahrt />
        </>
      )

    case 'faehre':
      return (
        <>
          {/*
            Die gefahrene Spur läuft von unten bis ans Wasser und über dem
            Schiff weiter - dazwischen ist sie nicht gezeichnet, und genau das
            ist die Auskunft: hier fährt man nicht selbst, hier wird man
            getragen. Der Unterschied zum Tunnel steht damit im Strich. Dort
            ist die Strasse gestrichelt, weil sie da ist und man sie nur nicht
            sieht; hier ist sie weg, und das Schiff ist der Weg.
          */}
          <line x1={MITTE} y1={UNTEN} x2={MITTE} y2={70} strokeWidth={STRICH_FAHRT} />
          {/*
            Wasser links und rechts des Rumpfs, nicht dahinter. Eine Welle, die
            unter dem Schiff durchläuft, kreuzt dessen Bordwand zweimal, und bei
            19 mm liegen diese Kreuzungen einen halben Millimeter auseinander -
            auf einer Kopie wird daraus ein Klumpen. Beiseite gesetzt trifft die
            Welle die Bordwand genau in der Wasserlinie und sagt dasselbe mit
            einer Kreuzung weniger.

            Die Wellen sind nicht Schmuck, sondern das Zeichen. Ohne sie liest
            sich derselbe Rumpf mit demselben Aufbau als Lastwagen oder als
            Häuschen - bei 19 mm nachgesehen, nicht vermutet.
          */}
          <path d="M 4 68 q 8 -6 16 0 t 16 0 M 64 68 q 8 -6 16 0 t 16 0" strokeWidth={STRICH_WEG} />
          {/* Rumpf mit einziehenden Bordwänden, darauf das Steuerhaus. */}
          <path d="M 20 50 L 80 50 L 72 70 L 28 70 Z" strokeWidth={2.6} />
          <path d="M 40 50 L 40 34 L 60 34 L 60 50" strokeWidth={2.4} />
          <line x1={MITTE} y1={34} x2={MITTE} y2={OBEN} strokeWidth={STRICH_FAHRT} />
          <PfeilHoch x={MITTE} />
          <Herkunft x={MITTE} />
        </>
      )

    case 'schotter':
      return (
        <>
          {/*
            Die Spur bleibt durchgezogen und dick. Der Belag ändert nichts
            daran, dass dies der gefahrene Weg ist - und die Spur zu
            unterbrechen oder zu stricheln hiesse, was der Tunnel sagt ("ist da,
            sieht man nicht") oder was die Fähre sagt ("hier fährst du nicht").
            Aufgelöst ist die Kante daneben: eine geteerte Strasse hat einen
            Rand, eine geschotterte franst aus. Dasselbe, was jede Karte tut,
            wenn sie einen Feldweg von einer Strasse unterscheidet.

            Ungleich lange Striche, und die beiden Seiten gegeneinander
            versetzt. Ein gleichmässiger Strich an beiden Rändern las sich bei
            19 mm als aufgemalte Markierung - also als das Gegenteil einer
            unbefestigten Strasse. Erst die Unregelmässigkeit macht daraus eine
            Kante, die es so genau nicht gibt.

            Die Ränder laufen über die ganze Höhe und begrenzen den Abschnitt
            nicht: wo der Schotter aufhört, weiss vorher niemand, und ein
            gezeichnetes Ende wäre eine Angabe, die keine Quelle hat. Das
            unterscheidet ihn von Tunnel und Kehren, deren Länge gemessen ist
            und im Hinweis steht.
          */}
          <line
            x1={32}
            y1={14}
            x2={32}
            y2={96}
            strokeWidth={STRICH_WEG}
            strokeDasharray="11 6 5 7"
          />
          <line
            x1={68}
            y1={14}
            x2={68}
            y2={96}
            strokeWidth={STRICH_WEG}
            strokeDasharray="11 6 5 7"
            strokeDashoffset={9}
          />
          <Durchfahrt />
        </>
      )

    case 'tunnel':
      return (
        <>
          <line x1={MITTE} y1={UNTEN} x2={MITTE} y2={66} strokeWidth={STRICH_FAHRT} />
          {/*
            Das Portal, unter dem man durchfährt. Der Bogen allein sah aus wie
            eine Brücke, über die man fährt - erst die beiden Widerlager
            machen daraus eine Öffnung, in die man hineinfährt.
          */}
          <path d="M 26 80 L 26 66 A 24 24 0 0 1 74 66 L 74 80" strokeWidth={2.4} />
          {/* Drinnen wird die Strasse gestrichelt: sie ist da, man sieht sie nur nicht. */}
          <line
            x1={MITTE}
            y1={66}
            x2={MITTE}
            y2={OBEN}
            strokeWidth={STRICH_FAHRT}
            strokeDasharray="8 7"
          />
          <PfeilHoch x={MITTE} />
          <Herkunft x={MITTE} />
        </>
      )

    case 'serpentine':
      return (
        <>
          {/*
            Vier Kehren, nicht fünfzig: das Zeichen sagt "hier wird gewendet",
            die Zahl steht im Hinweis. Der Abstand der Schenkel ist bei 19 mm
            rund 2.7 mm - enger gezeichnet laufen sie beim Kopieren ineinander.
          */}
          <path
            d={`M ${MITTE} ${UNTEN} L ${MITTE} 82 L 24 74 L 76 60 L 24 46 L 76 32 L ${MITTE} 24`}
            strokeWidth={STRICH_FAHRT}
          />
          <PfeilHoch x={MITTE} />
          <Herkunft x={MITTE} />
        </>
      )

    case 'bahn':
      return (
        <>
          {/* Zwei Schienen mit Schwellen: das Kartenzeichen für eine Bahnlinie. */}
          <line x1={6} y1={42} x2={94} y2={42} strokeWidth={2} />
          <line x1={6} y1={58} x2={94} y2={58} strokeWidth={2} />
          {[10, 22, 34, 66, 78, 90].map((x) => (
            <line key={x} x1={x} y1={42} x2={x} y2={58} strokeWidth={1.6} />
          ))}
          <Durchfahrt />
        </>
      )

    case 'ort':
      return (
        <>
          {/* Zwei Häuser mit Satteldach. Bei 19 mm ist eines gut 4 mm breit. */}
          <rect x={12} y={46} width={22} height={20} strokeWidth={STRICH_WEG} />
          <polyline points="10,46 23,33 36,46" strokeWidth={STRICH_WEG} />
          <rect x={66} y={50} width={22} height={20} strokeWidth={STRICH_WEG} />
          <polyline points="64,50 77,37 90,50" strokeWidth={STRICH_WEG} />
          <Durchfahrt />
        </>
      )

    case 'auffahrt':
      return (
        <>
          <Autobahn links={58} rechts={90} />
          {/* Die Rampe schwenkt ein und wird selbst zur gefahrenen Spur. */}
          <path
            d={`M 18 ${UNTEN} L 18 70 C 18 52 74 62 74 40 L 74 ${OBEN}`}
            strokeWidth={STRICH_FAHRT}
          />
          <PfeilHoch x={74} />
          <Herkunft x={18} />
        </>
      )

    case 'abfahrt':
      return (
        <>
          <Autobahn links={10} rechts={42} />
          <path
            d={`M 26 ${UNTEN} L 26 62 C 26 42 82 46 82 26 L 82 ${OBEN}`}
            strokeWidth={STRICH_FAHRT}
          />
          <PfeilHoch x={82} />
          <Herkunft x={26} />
        </>
      )

    default:
      return <Durchfahrt />
  }
}

/** Die gerade Strasse von unten nach oben, mit Punkt und Pfeil. */
function Durchfahrt() {
  return (
    <>
      <line x1={MITTE} y1={UNTEN} x2={MITTE} y2={OBEN} strokeWidth={STRICH_FAHRT} />
      <PfeilHoch x={MITTE} />
      <Herkunft x={MITTE} />
    </>
  )
}

/** Die beiden Fahrbahnkanten einer Autobahn, über die ganze Höhe. */
function Autobahn({ links, rechts }: { links: number; rechts: number }) {
  return (
    <>
      <line x1={links} y1={4} x2={links} y2={96} strokeWidth={2} />
      <line x1={rechts} y1={4} x2={rechts} y2={96} strokeWidth={2} />
    </>
  )
}

/** Der Punkt, aus dem man kommt. */
function Herkunft({ x }: { x: number }) {
  return <circle cx={x} cy={UNTEN} r={PUNKT_R} fill="currentColor" stroke="none" />
}

/** Der Pfeil am oberen Ende einer durchgehenden Strasse. */
function PfeilHoch({ x }: { x: number }) {
  return (
    <polygon
      points={`${String(x)},6 ${String(x - 7)},20 ${String(x + 7)},20`}
      fill="currentColor"
      stroke="none"
    />
  )
}
